/*
 * /producer/$producerId (page)
 */
import {
	Badge,
	Button,
	Card,
	CardContent,
	CardHeader,
	CardTitle,
	Container,
	Dialog,
	DialogContent,
	DialogFooter,
	DialogHeader,
	DialogTitle,
	Form,
	FormButtons,
	InputFormField,
	NumberInputFormField,
	Page,
	PageHeader,
	Placeholder,
	SelectFormField,
	Tabs,
	TabsContent,
	TabsList,
	TabsTrigger,
	Textarea,
	TextareaFormField,
	CheckboxFormField,
	toast,
} from "@emr/react-ui"
import { createFileRoute } from "@tanstack/react-router"
import { CheckCircle, FileText, Package, Plus, RefreshCw, Search, Star, TrendingUp, Upload, XCircle } from "lucide-react"
import { useRef, useState, useMemo } from "react"
import { z } from "zod"
import { ProducerForm } from "../../features/producer/producer-form"
import { trpc } from "../../trpc"
import type { Producer, Product } from "@emr/db/client"

export const Route = createFileRoute("/producer/$producerId")({
	component: ProducerPage,
})

/* ─── Product type options ─────────────────────────────────────────────── */

const PRODUCT_TYPE_OPTIONS = [
	{ label: "Dried Flower", value: "1" },
	{ label: "Oil", value: "2" },
	{ label: "Concentrate", value: "3" },
	{ label: "Topical Ointment", value: "4" },
	{ label: "Oral Spray", value: "5" },
	{ label: "Seeds", value: "6" },
	{ label: "Edible", value: "7" },
	{ label: "Capsule", value: "8" },
	{ label: "Tablet", value: "9" },
	{ label: "Patch", value: "10" },
	{ label: "Tincture", value: "11" },
	{ label: "Suppository", value: "12" },
]

const MEASUREMENT_OPTIONS = [
	{ label: "Grams (g)", value: "1" },
	{ label: "Millilitres (ml)", value: "2" },
	{ label: "Each (unit)", value: "3" },
]

/* ─── Product form schema ──────────────────────────────────────────────── */

const productFormSchema = z.object({
	name: z.string().min(1, "Name is required"),
	productType: z.coerce.number().int().min(1),
	quantity: z.coerce.number().min(0).optional(),
	measurement: z.coerce.number().int().optional(),
	description: z.string().optional().nullable(),
	pdfFieldName: z.string().max(128).optional().nullable(),
	preferred: z.boolean().default(false),
	popular: z.boolean().default(false),
	discontinued: z.boolean().default(false),
	currentlyUnavailable: z.boolean().default(false),
})

type ProductFormValues = z.infer<typeof productFormSchema>

/* ─── Page ─────────────────────────────────────────────────────────────── */

function ProducerPage() {
	const { producerId } = Route.useParams()
	const [showAddProduct, setShowAddProduct] = useState(false)
	const [syncOpen, setSyncOpen] = useState(false)
	const [productSearch, setProductSearch] = useState("")

	const { data: producer, isLoading: producerLoading } =
		trpc.producer.getByFilter.useQuery({ where: { id: producerId } })

	const {
		data: products,
		isLoading: productsLoading,
		refetch: refetchProducts,
	} = trpc.product.getAllByFilter.useQuery({
		where: { producerId, active: true },
	})

	const filteredProducts = useMemo(() => {
		const q = productSearch.trim().toLowerCase()
		const list = (products ?? []).filter((p) => !q || p.name.toLowerCase().includes(q))
		return [...list].sort((a, b) => {
			const scoreA = (a as any).preferred ? 2 : (a as any).popular ? 1 : 0
			const scoreB = (b as any).preferred ? 2 : (b as any).popular ? 1 : 0
			if (scoreA !== scoreB) return scoreB - scoreA
			return a.name.localeCompare(b.name)
		})
	}, [products, productSearch])

	const createProduct = trpc.product.create.useMutation({
		onSuccess: () => {
			refetchProducts()
			setShowAddProduct(false)
			toast.success("Product added")
		},
		onError: (e) => toast.error(e.message),
	})

	async function handleAddProduct(values: ProductFormValues) {
		try {
			await createProduct.mutateAsync({ ...values, producerId })
		} catch (e: any) {
			return e?.message ?? "An error occurred"
		}
	}

	const isLoading = producerLoading || productsLoading

	if (isLoading) {
		return (
			<Page>
				<Container width="xl" padding="lg">
					<Placeholder title="Loading..." />
				</Container>
			</Page>
		)
	}

	return (
		<Page>
			<Container width="xl" padding="lg" gap="lg">
				<PageHeader title={producer?.name ?? "Producer"} />

				<Tabs defaultValue={producer ? "details" : "products"}>
					<TabsList>
						{producer && <TabsTrigger value="details">Details</TabsTrigger>}
						<TabsTrigger value="products">
							Products
							{products && products.length > 0 && (
								<Badge className="ml-2">{products.length}</Badge>
							)}
						</TabsTrigger>
						{producer && (
							<TabsTrigger value="registration">
								<FileText className="w-4 h-4 mr-1.5" />
								Registration Form
							</TabsTrigger>
						)}
					</TabsList>

					{/* ── Details tab ── */}
					{producer && (
						<TabsContent value="details" className="mt-4">
							<Card>
								<CardHeader>
									<CardTitle>Producer Information</CardTitle>
								</CardHeader>
								<CardContent>
									<ProducerForm producer={producer} />
								</CardContent>
							</Card>
						</TabsContent>
					)}

					{/* ── Registration Form tab ── */}
					{producer && (
						<TabsContent value="registration" className="mt-4">
							<RegistrationFormTab producer={producer} />
						</TabsContent>
					)}

					{/* ── Products tab ── */}
					<TabsContent value="products" className="mt-4 flex flex-col gap-4">

						{/* Add product form */}
						{showAddProduct ? (
							<Card>
								<CardHeader>
									<CardTitle>Add Product</CardTitle>
								</CardHeader>
								<CardContent>
									<Form<ProductFormValues>
										schema={productFormSchema}
										defaultValues={{
											preferred: false,
											popular: false,
											discontinued: false,
											currentlyUnavailable: false,
										}}
										onSubmit={handleAddProduct}
										className="flex flex-col gap-4"
									>
										<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
											<InputFormField
												name="name"
												label="Product Name"
												placeholder="e.g. Indica Dried Flower"
												required
											/>
											<SelectFormField
												name="productType"
												label="Product Type"
												options={PRODUCT_TYPE_OPTIONS}
												placeholder="Select type"
												required
											/>
										</div>

										<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
											<NumberInputFormField
												name="quantity"
												label="Quantity"
												placeholder="0"
												min={0}
												step={0.1}
											/>
											<SelectFormField
												name="measurement"
												label="Measurement"
												options={MEASUREMENT_OPTIONS}
												placeholder="Select unit"
											/>
										</div>

										<TextareaFormField
											name="description"
											label="Description"
											placeholder="Product description..."
										/>

										<InputFormField
											name="pdfFieldName"
											label="PDF Field Name"
											placeholder="e.g. cb_Topical1"
											helperText="AcroForm checkbox field name in the topical prescription PDF template."
										/>

										<div className="flex flex-row gap-6">
											<CheckboxFormField name="preferred" label="Preferred" />
											<CheckboxFormField name="popular" label="Popular" />
											<CheckboxFormField name="discontinued" label="Discontinued" />
											<CheckboxFormField name="currentlyUnavailable" label="Currently Unavailable" />
										</div>

										<div className="flex gap-2">
											<FormButtons submitLabel="Add Product" />
											<Button
												type="button"
												variant="outline"
												onClick={() => setShowAddProduct(false)}
											>
												Cancel
											</Button>
										</div>
									</Form>
								</CardContent>
							</Card>
						) : (
							<div className="flex justify-end gap-2">
								<Button variant="outline" onClick={() => setSyncOpen(true)}>
									<RefreshCw className="w-4 h-4 mr-1" />
									Sync from JSON
								</Button>
								<Button onClick={() => setShowAddProduct(true)}>
									<Plus className="w-4 h-4 mr-1" />
									Add Product
								</Button>
							</div>
						)}

						{/* Search bar */}
						{products && products.length > 0 && (
							<div className="relative">
								<Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
								<input
									type="text"
									value={productSearch}
									onChange={(e) => setProductSearch(e.target.value)}
									placeholder="Search products…"
									className="w-full h-9 pl-9 pr-3 rounded-md border border-input bg-background text-sm shadow-sm focus:outline-none focus:ring-1 focus:ring-ring"
								/>
							</div>
						)}

						{/* Product list */}
						{!products || products.length === 0 ? (
							<Placeholder title="No products yet" />
						) : filteredProducts.length === 0 ? (
							<p className="text-sm text-muted-foreground text-center py-4">No products match "{productSearch}"</p>
						) : (
							<div className="flex flex-col gap-3">
								{filteredProducts.map((product) => (
									<ProductCard key={product.id} product={product} onUpdated={refetchProducts} />
								))}
							</div>
						)}
					</TabsContent>
				</Tabs>
			</Container>

			<SyncProductsDialog
				producerId={producerId}
				open={syncOpen}
				onOpenChange={setSyncOpen}
				onSynced={refetchProducts}
			/>
		</Page>
	)
}


/* ─── Sync Products Dialog ────────────────────────────────────────────── */

const SYNC_EXAMPLE = JSON.stringify(
	[{ name: "Product Name", productType: 1, quantity: 3.5, measurement: 1, description: "Optional" }],
	null,
	2,
)

const PRODUCT_TYPE_MAP: Record<number, string> = {
	1: "Dried Flower", 2: "Oil", 3: "Concentrate", 4: "Topical Ointment",
	5: "Oral Spray", 6: "Seeds", 7: "Edible", 8: "Capsule",
	9: "Tablet", 10: "Patch", 11: "Tincture", 12: "Suppository",
}

const syncItemSchema = z.object({
	name: z.string().min(1),
	productType: z.number().int().min(1).max(12),
	quantity: z.number().optional(),
	measurement: z.number().int().min(1).max(3).optional(),
	description: z.string().optional(),
})

function SyncProductsDialog({
	producerId,
	open,
	onOpenChange,
	onSynced,
}: {
	producerId: string
	open: boolean
	onOpenChange: (v: boolean) => void
	onSynced: () => void
}) {
	const [json, setJson] = useState("")
	const [parseError, setParseError] = useState<string | null>(null)
	const [result, setResult] = useState<{ created: string[]; updated: string[]; markedUnavailable: string[] } | null>(null)

	const syncProducts = trpc.product.syncProducts.useMutation({
		onSuccess: (data) => {
			setResult(data)
			onSynced()
		},
		onError: (e) => toast.error(e.message),
	})

	function handleClose() {
		setJson("")
		setParseError(null)
		setResult(null)
		onOpenChange(false)
	}

	function handleSync() {
		setParseError(null)
		let parsed: unknown
		try {
			parsed = JSON.parse(json)
		} catch {
			setParseError("Invalid JSON — check for missing commas or brackets.")
			return
		}
		if (!Array.isArray(parsed)) {
			setParseError("JSON must be an array [ … ] of product objects.")
			return
		}
		const validated = z.array(syncItemSchema).safeParse(parsed)
		if (!validated.success) {
			const first = validated.error.errors[0]
			setParseError(`Row ${(first.path[0] as number ?? 0) + 1}: ${first.message} (${first.path.join(".")})`)
			return
		}
		syncProducts.mutate({ producerId, products: validated.data })
	}

	return (
		<Dialog open={open} onOpenChange={handleClose}>
			<DialogContent className="sm:max-w-2xl">
				<DialogHeader>
					<DialogTitle>Sync Products from JSON</DialogTitle>
				</DialogHeader>

				{result ? (
					<div className="flex flex-col gap-3 py-2 text-sm">
						<SyncResultGroup label="Created" items={result.created} color="text-green-600" />
						<SyncResultGroup label="Updated" items={result.updated} color="text-blue-600" />
						<SyncResultGroup label="Marked Unavailable" items={result.markedUnavailable} color="text-amber-600" />
						{result.created.length === 0 && result.updated.length === 0 && result.markedUnavailable.length === 0 && (
							<p className="text-muted-foreground">No changes — products already match.</p>
						)}
					</div>
				) : (
					<div className="flex flex-col gap-3 py-2">
						<p className="text-sm text-muted-foreground">
							Paste a JSON array. New products are created, existing ones updated, and any DB
							product absent from the list is marked <strong>unavailable</strong>.
						</p>
						<div className="rounded border bg-muted p-3 text-xs font-mono text-muted-foreground">
							<p className="font-semibold text-foreground mb-1">productType:</p>
							<div className="grid grid-cols-3 gap-x-4">
								{Object.entries(PRODUCT_TYPE_MAP).map(([k, v]) => (
									<span key={k}>{k} = {v}</span>
								))}
							</div>
							<p className="font-semibold text-foreground mt-2">measurement: 1=g · 2=ml · 3=each</p>
						</div>
						<Textarea
							placeholder={SYNC_EXAMPLE}
							value={json}
							onChange={(e) => { setJson(e.target.value); setParseError(null) }}
							rows={12}
							className="font-mono text-xs"
						/>
						{parseError && <p className="text-sm text-destructive">{parseError}</p>}
					</div>
				)}

				<DialogFooter>
					{result ? (
						<Button onClick={handleClose}>Done</Button>
					) : (
						<>
							<Button variant="outline" onClick={handleClose}>Cancel</Button>
							<Button onClick={handleSync} disabled={!json.trim() || syncProducts.isPending}>
								{syncProducts.isPending ? "Syncing…" : "Sync Products"}
							</Button>
						</>
					)}
				</DialogFooter>
			</DialogContent>
		</Dialog>
	)
}

function SyncResultGroup({ label, items, color }: { label: string; items: string[]; color: string }) {
	if (items.length === 0) return null
	return (
		<div>
			<p className={`font-semibold ${color}`}>{label} ({items.length})</p>
			<ul className="mt-1 ml-4 list-disc text-muted-foreground">
				{items.map((name) => <li key={name}>{name}</li>)}
			</ul>
		</div>
	)
}

/* ─── Registration Form tab ────────────────────────────────────────────── */

function RegistrationFormTab({ producer }: { producer: Producer }) {
	const utils = trpc.useUtils()
	const fileInputRef = useRef<HTMLInputElement>(null)
	const [testPatientId, setTestPatientId] = useState("")
	const [diagSteps, setDiagSteps] = useState<Array<{ name: string; ok: boolean; detail: string }> | null>(null)

	const getUploadUrl = trpc.producer.getPdfUploadUrl.useMutation()
	const updateProducer = trpc.producer.update.useMutation({
		onSuccess: () => {
			utils.producer.getByFilter.invalidate()
			toast.success("Registration form uploaded")
		},
		onError: (e) => toast.error(e.message),
	})
	const testPdf = trpc.producer.testPdf.useMutation({
		onSuccess: (data) => setDiagSteps(data.steps),
		onError: (e) => toast.error(e.message),
	})

	const getPdfInfo = trpc.producer.getPdfInfo.useQuery(
		{ producerId: producer.id },
		{ enabled: false },
	)
	const getPdfFields = trpc.producer.getPdfFields.useQuery(
		{ producerId: producer.id },
		{ enabled: false },
	)

	async function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
		const file = e.target.files?.[0]
		e.target.value = ""
		if (!file || file.type !== "application/pdf") {
			toast.error("Please select a PDF file")
			return
		}
		try {
			const { uploadUrl, s3Key } = await getUploadUrl.mutateAsync({
				producerId: producer.id,
				fileName: file.name,
			})
			const res = await fetch(uploadUrl, {
				method: "PUT",
				body: file,
				headers: { "Content-Type": "application/pdf" },
			})
			if (!res.ok) throw new Error(`S3 upload failed: ${res.status} ${res.statusText}`)
			await updateProducer.mutateAsync({ id: producer.id, pdfTemplateS3Key: s3Key })
		} catch (err: any) {
			console.error("[pdf-upload] error:", err)
			toast.error(`Upload failed: ${err?.message ?? "please try again"}`)
		}
	}

	const hasTemplate = Boolean(producer.pdfTemplateS3Key)
	const isUploading = getUploadUrl.isPending || updateProducer.isPending

	return (
		<Card>
			<CardHeader>
				<CardTitle>Registration Form Template</CardTitle>
			</CardHeader>
			<CardContent className="flex flex-col gap-6">
				<p className="text-sm text-muted-foreground">
					Upload this producer's PDF registration form. When a prescription is saved for
					this producer, the form will be auto-filled with patient and practitioner data
					and saved as an "LP Registration" attachment on the patient's file.
				</p>

				<div className="flex items-center gap-3 flex-wrap">
					{hasTemplate ? (
						<>
							<div className="flex items-center gap-2 text-sm text-green-600 dark:text-green-400">
								<FileText className="w-4 h-4" />
								Template uploaded
							</div>
							<Button
								variant="outline"
								size="sm"
								onClick={() => fileInputRef.current?.click()}
								disabled={isUploading}
							>
								<Upload className="w-4 h-4 mr-1.5" />
								Replace PDF
							</Button>
						</>
					) : (
						<Button
							variant="secondary"
							onClick={() => fileInputRef.current?.click()}
							disabled={isUploading}
						>
							<Upload className="w-4 h-4 mr-1.5" />
							{isUploading ? "Uploading…" : "Upload PDF Template"}
						</Button>
					)}
					<input
						ref={fileInputRef}
						type="file"
						accept="application/pdf"
						className="hidden"
						onChange={handleFileChange}
					/>
				</div>

				{hasTemplate && (
					<div className="rounded-md border bg-muted/30 p-4 text-sm flex flex-col gap-3">
						{/* Field names (AcroForm) */}
						<div className="flex flex-col gap-1">
							<p className="font-medium">PDF Form Fields</p>
							{getPdfFields.data ? (
								getPdfFields.data.error ? (
									<p className="text-xs text-destructive">{getPdfFields.data.error}</p>
								) : getPdfFields.data.fields.length === 0 ? (
									<p className="text-xs text-muted-foreground">No AcroForm fields detected — PDF uses coordinate-based fill</p>
								) : (
									<ul className="text-xs font-mono text-muted-foreground space-y-0.5 max-h-48 overflow-y-auto">
										{getPdfFields.data.fields.map((f) => (
											<li key={f.name}><span className="text-foreground">{f.name}</span> <span className="opacity-50">({f.type})</span></li>
										))}
									</ul>
								)
							) : (
								<Button
									variant="ghost"
									size="sm"
									className="self-start text-xs"
									onClick={() => getPdfFields.refetch()}
									disabled={getPdfFields.isFetching}
								>
									{getPdfFields.isFetching ? "Reading…" : "List PDF fields"}
								</Button>
							)}
						</div>

						{/* Page dimensions */}
						<div className="flex flex-col gap-1">
							<p className="font-medium">Page dimensions</p>
							{getPdfInfo.data ? (
								<ul className="text-xs font-mono text-muted-foreground space-y-0.5">
									{getPdfInfo.data.pages.map((p) => (
										<li key={p.page}>
											Page {p.page + 1}: {p.width.toFixed(1)} × {p.height.toFixed(1)} pt
										</li>
									))}
								</ul>
							) : (
								<Button
									variant="ghost"
									size="sm"
									className="self-start text-xs"
									onClick={() => getPdfInfo.refetch()}
									disabled={getPdfInfo.isFetching}
								>
									{getPdfInfo.isFetching ? "Reading…" : "Show page dimensions"}
								</Button>
							)}
						</div>
					</div>
				)}

				{/* ── PDF Pipeline Diagnostics ── */}
				<div className="rounded-md border p-4 flex flex-col gap-3">
					<p className="text-sm font-medium">PDF Pipeline Diagnostics</p>
					<p className="text-xs text-muted-foreground">
						Enter a patient ID to test each step of the PDF generation pipeline without saving anything.
					</p>
					<div className="flex gap-2 items-center">
						<input
							type="text"
							value={testPatientId}
							onChange={(e) => setTestPatientId(e.target.value)}
							placeholder="Patient UUID"
							className="flex-1 h-9 rounded-md border bg-background px-3 text-sm font-mono"
						/>
						<Button
							type="button"
							variant="outline"
							size="sm"
							disabled={!testPatientId.trim() || testPdf.isPending}
							onClick={() => {
								setDiagSteps(null)
								testPdf.mutate({ producerId: producer.id, patientId: testPatientId.trim() })
							}}
						>
							{testPdf.isPending ? "Running…" : "Run Test"}
						</Button>
					</div>
					{diagSteps && (
						<ul className="flex flex-col gap-1.5 mt-1">
							{diagSteps.map((s, i) => (
								<li key={i} className="flex items-start gap-2 text-xs">
									{s.ok
										? <CheckCircle className="w-3.5 h-3.5 text-green-500 shrink-0 mt-0.5" />
										: <XCircle className="w-3.5 h-3.5 text-destructive shrink-0 mt-0.5" />}
									<span>
										<span className="font-medium">{s.name}:</span>{" "}
										<span className={s.ok ? "text-muted-foreground" : "text-destructive"}>
											{s.detail}
										</span>
									</span>
								</li>
							))}
						</ul>
					)}
				</div>
			</CardContent>
		</Card>
	)
}

/* ─── Product card ─────────────────────────────────────────────────────── */

function ProductCard({
	product,
	onUpdated,
}: {
	product: Product
	onUpdated: () => void
}) {
	const [editing, setEditing] = useState(false)
	const updateProduct = trpc.product.update.useMutation({
		onSuccess: () => {
			onUpdated()
			setEditing(false)
			toast.success("Product updated")
		},
		onError: (e) => toast.error(e.message),
	})

	const typeName =
		PRODUCT_TYPE_OPTIONS.find((o) => o.value === String(product.productType))
			?.label ?? `Type ${product.productType}`
	const measurementLabel =
		MEASUREMENT_OPTIONS.find((o) => o.value === String(product.measurement))?.label ?? null

	async function handleUpdate(values: ProductFormValues) {
		try {
			await updateProduct.mutateAsync({ id: product.id, ...values } as any)
		} catch (e: any) {
			return e?.message ?? "An error occurred"
		}
	}

	if (editing) {
		return (
			<Card>
				<CardHeader>
					<CardTitle className="text-base">{product.name}</CardTitle>
				</CardHeader>
				<CardContent>
					<Form<ProductFormValues>
						schema={productFormSchema}
						defaultValues={{
							name: product.name,
							productType: String(product.productType) as any,
							quantity: product.quantity ?? undefined,
							measurement: product.measurement != null ? (String(product.measurement) as any) : undefined,
							description: product.description,
							pdfFieldName: (product as any).pdfFieldName ?? undefined,
							preferred: (product as any).preferred ?? false,
							popular: (product as any).popular ?? false,
							discontinued: product.discontinued,
							currentlyUnavailable: product.currentlyUnavailable,
						}}
						onSubmit={handleUpdate}
						className="flex flex-col gap-4"
					>
						<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
							<InputFormField name="name" label="Product Name" required />
							<SelectFormField
								name="productType"
								label="Product Type"
								options={PRODUCT_TYPE_OPTIONS}
								required
							/>
						</div>
						<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
							<NumberInputFormField
								name="quantity"
								label="Quantity"
								min={0}
								step={0.1}
							/>
							<SelectFormField
								name="measurement"
								label="Measurement"
								options={MEASUREMENT_OPTIONS}
							/>
						</div>
						<TextareaFormField name="description" label="Description" />
						<InputFormField
							name="pdfFieldName"
							label="PDF Field Name"
							placeholder="e.g. cb_Topical1"
							helperText="AcroForm checkbox field name in the topical prescription PDF template."
						/>
						<div className="flex flex-row gap-6">
							<CheckboxFormField name="preferred" label="Preferred" />
							<CheckboxFormField name="popular" label="Popular" />
							<CheckboxFormField name="discontinued" label="Discontinued" />
							<CheckboxFormField name="currentlyUnavailable" label="Currently Unavailable" />
						</div>
						<div className="flex gap-2">
							<FormButtons submitLabel="Save" />
							<Button type="button" variant="outline" onClick={() => setEditing(false)}>
								Cancel
							</Button>
						</div>
					</Form>
				</CardContent>
			</Card>
		)
	}

	return (
		<Card>
			<CardContent className="flex items-center justify-between py-4">
				<div className="flex items-center gap-3">
					<Package className="w-5 h-5 text-muted-foreground shrink-0" />
					<div>
						<p className="font-medium">{product.name}</p>
						<p className="text-sm text-muted-foreground">
							{typeName}
							{product.quantity != null && ` · ${product.quantity}${measurementLabel ? ` ${measurementLabel}` : ""}`}
							{(product as any).pdfFieldName && (
								<span className="ml-2 font-mono text-xs text-muted-foreground/70">
									{(product as any).pdfFieldName}
								</span>
							)}
						</p>
					</div>
				</div>
				<div className="flex items-center gap-2">
					{(product as any).preferred && (
						<Badge className="bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300 border-0 gap-1">
							<Star className="w-3 h-3" /> Preferred
						</Badge>
					)}
					{(product as any).popular && !(product as any).preferred && (
						<Badge className="bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300 border-0 gap-1">
							<TrendingUp className="w-3 h-3" /> Popular
						</Badge>
					)}
					{product.discontinued && (
						<Badge variant="destructive">Discontinued</Badge>
					)}
					{product.currentlyUnavailable && (
						<Badge variant="secondary">Unavailable</Badge>
					)}
					<Button
						variant="outline"
						size="sm"
						onClick={() => setEditing(true)}
					>
						Edit
					</Button>
				</div>
			</CardContent>
		</Card>
	)
}
